/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Florian Hartl <f.hartl@techdivision.com>
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box, List, ListItem, ListItemIcon, ListItemText, useMediaQuery,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Button from '@mui/material/Button';
import theme from '../../../../../Config/ThemeConfig';
import { useContractServices } from './useContractServices';
import { inclusiveServicesConfig } from './config';

/**
 * ContactForm Component
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export function ContractServices({
  services,
  limitation,
}) {
  const { t } = useTranslation('translations');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const limitationAdjustment = isMobile ? 1 : limitation;
  const {
    showAll,
    handleShowMoreClick,
  } = useContractServices();

  // for basic contracts with no extra services use the inclusiveServicesConfig instead
  let servicesToRender = services;
  if (!servicesToRender.length) {
    servicesToRender = inclusiveServicesConfig;
  }

  const itemsToRender = showAll ? servicesToRender : servicesToRender.slice(0, limitationAdjustment);

  return (
    <Box p={1}>
      <List dense>
        {itemsToRender.map((service) => (
          <ListItem key={service.name} sx={{ px: 0 }}>
            <ListItemIcon sx={{
              minWidth: 0,
              mr: '1rem',
            }}
            >
              <CheckCircleOutlineIcon sx={{ fill: theme.palette.primary.main }} />
            </ListItemIcon>
            <ListItemText primary={service.name} />
          </ListItem>
        ))}
      </List>
      {servicesToRender.length > limitationAdjustment && (
        <Button
          startIcon={showAll ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          sx={{ mb: 1 }}
          onClick={handleShowMoreClick}
          color="primary"
          aria-expanded={showAll}
          data-block-event
        >
          {showAll ? t('showLess') : t('showMore')}
        </Button>
      )}
    </Box>
  );
}

ContractServices.propTypes = {
  services: PropTypes.array.isRequired,
  limitation: PropTypes.number,
};

ContractServices.defaultProps = {
  limitation: 25,
};
